/**
 * Closure periods — the admin's "we're shut between these datetimes" config.
 *
 * Replaces the old single-date `settings.holidays` list. A closure NEVER blocks
 * an order: it greys the days out in the checkout picker, pushes the earliest
 * selectable date onto the resume date, and warns the customer. There is no
 * server-side refusal — see `docs/superpowers/specs/2026-08-07-holiday-closures-design.md`.
 *
 * Rules encoded here (all of them, once — this module is the single source):
 *
 *  1. A calendar date is unavailable if the closure overlaps it AT ALL. Closing
 *     at 17:00 on 24 Dec makes the whole of 24 Dec unavailable. The preferred-date
 *     field is date-only and a part-day is not a day we can fulfil; the times
 *     exist to word the notice precisely and to answer "are we shut right now?".
 *  2. Resume date per product line = end calendar date + `resumeDays[line]`.
 *  3. A mixed cart takes the LONGEST lead — the order ships as one job.
 *  4. The unavailable range is `[start date … resumeDate − 1 day]`, inclusive, so
 *     the closure and its resume ramp grey out as one contiguous block.
 *  5. The earliest selectable date is the first date at or after the normal
 *     earliest that falls in no unavailable range.
 *  6. A closure is announced from `start − noticeDaysBefore` until its resume
 *     date. Announcement gates the customer-facing PROSE only — never the
 *     disabled dates. `publicClosures` (announced, with reasons) is what any
 *     surface that writes a SENTENCE reads; `pickerClosures` (all unfinished
 *     closures, reason + admin id stripped while un-announced) is what the
 *     date picker reads. Serving the narrow list to the picker was CLOSURE-A:
 *     a closed day stayed fully selectable for the whole pre-notice window.
 *
 * ⚠ ALL date reasoning is done in **Europe/London** via `Intl`, following the
 * `ukNow` pattern in `lib/fulfilment/delivery-date-validation.ts`. Cloudflare Workers run in
 * UTC and customers can be anywhere, so a naive `new Date()` misjudges every
 * boundary date for up to an hour a day (and the boundary dates are the entire
 * point of this module).
 */

import { addDays, startOfDay } from "date-fns";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type ProductLine = "fresh" | "preserved" | "crochet" | "accessories";

export interface ClosurePeriod {
  id: string;
  /** Europe/London local datetime, `YYYY-MM-DDTHH:mm`. */
  start: string;
  end: string;
  reason: string;
  /** Per-closure override of the policy default. */
  noticeDaysBefore?: number;
  resumeDays?: Partial<Record<ProductLine, number>>;
}

export interface ClosurePolicy {
  noticeDaysBefore: number;
  resumeDays: Record<ProductLine, number>;
}

/** Canonical order — used whenever a set of lines is rendered or reduced. */
export const PRODUCT_LINES: readonly ProductLine[] = [
  "fresh",
  "preserved",
  "crochet",
  "accessories",
];

export const DEFAULT_CLOSURE_POLICY: ClosurePolicy = {
  noticeDaysBefore: 14,
  resumeDays: { fresh: 2, preserved: 1, crochet: 1, accessories: 1 },
};

/**
 * Cart-item category strings, canonical copy.
 *
 * These are the SAME literals the checkout matches on (they were duplicated in
 * `app/(main)/checkout/CheckoutClient.tsx`); exported here so the closure rules
 * and the checkout classify a cart identically by construction rather than by
 * coincidence. Both casings are listed because product rows carry either.
 * "Bags"/"bags" is a legacy safety net for accessory rows that never flipped.
 */
export const PRESERVED_CATEGORIES: readonly string[] = ["Preserved", "preserved"];
export const CROCHET_CATEGORIES: readonly string[] = ["Crochet", "crochet"];
export const ACCESSORIES_CATEGORIES: readonly string[] = [
  "Accessories",
  "accessories",
  "Bags",
  "bags",
];
export const LEEDS_CATEGORIES: readonly string[] = ["Fresh", "fresh"];

/** Max characters of the admin's free-text reason shown to a customer.
 *  Matches the long-standing `MAX_REASON_LEN` — the reason is free text that
 *  lands in a PUBLIC JSON response. */
const MAX_REASON_LEN = 80;

/** Hard bound on any forward walk, so a pathological config can't spin. */
export const DEFAULT_HORIZON_DAYS = 365;

const MAX_NOTICE_DAYS = 365;
const MAX_RESUME_DAYS = 90;

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/* ------------------------------------------------------------------ */
/*  Local-datetime parsing (floating Europe/London wall-clock values)  */
/* ------------------------------------------------------------------ */

interface LocalStamp {
  /** `YYYY-MM-DD`. */
  dateStr: string;
  /** Floating Date at local midnight of that calendar day — safe for date-fns. */
  day: Date;
  /** Minutes past midnight, 0–1439. */
  minutes: number;
  /**
   * Comparable ordinal = (days since epoch) × 1440 + minutes. Derived through
   * `Date.UTC` on purpose: a wall-clock ordinal built from a LOCAL Date's
   * `getTime()` is wrong on the host's own DST-transition days (adjacent
   * midnights are 23h or 25h apart there), which would silently mis-order two
   * closures once or twice a year.
   */
  ordinal: number;
}

const STAMP_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?)?$/;

/** Parse `YYYY-MM-DD[THH:mm]` as a floating local wall-clock stamp, or null. */
function parseLocalStamp(v: unknown): LocalStamp | null {
  if (typeof v !== "string") return null;
  const m = STAMP_RE.exec(v.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const dayOfMonth = Number(m[3]);
  const hour = m[4] === undefined ? 0 : Number(m[4]);
  const minute = m[5] === undefined ? 0 : Number(m[5]);
  if (month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) return null;
  if (hour > 23 || minute > 59) return null;
  const day = startOfDay(new Date(year, month - 1, dayOfMonth));
  // Rejects overflow dates (2026-02-31 rolls to March) and two-digit-year
  // coercion (`new Date(26, …)` → 1926).
  if (
    Number.isNaN(day.getTime()) ||
    day.getFullYear() !== year ||
    day.getMonth() !== month - 1 ||
    day.getDate() !== dayOfMonth
  ) {
    return null;
  }
  const minutes = hour * 60 + minute;
  const dayNumber = Math.round(Date.UTC(year, month - 1, dayOfMonth) / 86400000);
  return {
    dateStr: formatDateKey(day),
    day,
    minutes,
    ordinal: dayNumber * 1440 + minutes,
  };
}

/** `YYYY-MM-DD` from a floating local Date. */
function formatDateKey(d: Date): string {
  const y = String(d.getFullYear()).padStart(4, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DDTHH:mm` — the canonical stored form. */
function formatStamp(s: LocalStamp): string {
  const hh = String(Math.floor(s.minutes / 60)).padStart(2, "0");
  const mm = String(s.minutes % 60).padStart(2, "0");
  return `${s.dateStr}T${hh}:${mm}`;
}

/* ------------------------------------------------------------------ */
/*  UK "now"                                                           */
/* ------------------------------------------------------------------ */

interface UkNow {
  /** `YYYY-MM-DD` — the UK calendar date right now. */
  dateStr: string;
  /** Floating Date at UK-calendar midnight today. */
  today: Date;
  /** Comparable ordinal, same scale as `LocalStamp.ordinal`. */
  ordinal: number;
}

/** Resolve an instant into the UK-local calendar date + wall-clock ordinal. */
function ukNow(now: Date): UkNow {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  let hour = Number(get("hour"));
  const minute = Number(get("minute"));
  // en-GB can emit "24" for midnight under hour12:false.
  if (!Number.isFinite(hour) || hour === 24) hour = 0;
  const stamp = parseLocalStamp(
    `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(Number.isFinite(minute) ? minute : 0).padStart(2, "0")}`
  );
  if (!stamp) {
    // Unreachable in practice; degrade to the host clock rather than throw.
    const fallback = startOfDay(now);
    return {
      dateStr: formatDateKey(fallback),
      today: fallback,
      ordinal: Math.round(Date.UTC(fallback.getFullYear(), fallback.getMonth(), fallback.getDate()) / 86400000) * 1440,
    };
  }
  return { dateStr: stamp.dateStr, today: stamp.day, ordinal: stamp.ordinal };
}

/** The UK calendar date, `YYYY-MM-DD`, for an instant. Exported for callers that
 *  need "today" on the same clock the closure rules use. */
export function ukTodayKey(now: Date): string {
  return ukNow(now).dateStr;
}

/* ------------------------------------------------------------------ */
/*  Normalisation                                                      */
/* ------------------------------------------------------------------ */

/** Trim / collapse / cap the admin's free-text reason before a customer sees it. */
export function sanitiseClosureReason(reason: string | null | undefined): string {
  return (reason ?? "").replace(/\s+/g, " ").trim().slice(0, MAX_REASON_LEN);
}

function clampInt(v: unknown, min: number, max: number): number | undefined {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  const n = Math.round(v);
  if (n < min || n > max) return undefined;
  return n;
}

/** Always returns a fully-populated policy, so callers never branch on absence. */
export function normalizeClosurePolicy(input: unknown): ClosurePolicy {
  const o = (input ?? {}) as Record<string, unknown>;
  const rawResume = (o.resumeDays ?? {}) as Record<string, unknown>;
  const resumeDays = {} as Record<ProductLine, number>;
  for (const line of PRODUCT_LINES) {
    resumeDays[line] =
      clampInt(rawResume[line], 0, MAX_RESUME_DAYS) ?? DEFAULT_CLOSURE_POLICY.resumeDays[line];
  }
  return {
    noticeDaysBefore:
      clampInt(o.noticeDaysBefore, 0, MAX_NOTICE_DAYS) ?? DEFAULT_CLOSURE_POLICY.noticeDaysBefore,
    resumeDays,
  };
}

function normalizeResumeOverrides(input: unknown): Partial<Record<ProductLine, number>> | undefined {
  if (!input || typeof input !== "object") return undefined;
  const o = input as Record<string, unknown>;
  const out: Partial<Record<ProductLine, number>> = {};
  let any = false;
  for (const line of PRODUCT_LINES) {
    const n = clampInt(o[line], 0, MAX_RESUME_DAYS);
    if (n !== undefined) {
      out[line] = n;
      any = true;
    }
  }
  return any ? out : undefined;
}

/**
 * Validate a stored closure array.
 *
 * Malformed rows — unparseable datetime, `end` before `start` — are DROPPED
 * rather than shipped. An Invalid Date reaching the picker's disabled matcher is
 * worse than a missing closure: it disables nothing and logs nothing.
 */
export function normalizeClosures(input: unknown): ClosurePeriod[] {
  if (!Array.isArray(input)) return [];
  const out: ClosurePeriod[] = [];
  for (const item of input) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const start = parseLocalStamp(o.start);
    const end = parseLocalStamp(o.end);
    if (!start || !end) continue;
    if (end.ordinal < start.ordinal) continue;
    const startStr = formatStamp(start);
    const endStr = formatStamp(end);
    const id =
      typeof o.id === "string" && o.id.trim()
        ? o.id.trim()
        : `closure-${startStr.replace(/\D/g, "")}-${endStr.replace(/\D/g, "")}`;
    out.push({
      id,
      start: startStr,
      end: endStr,
      reason: sanitiseClosureReason(typeof o.reason === "string" ? o.reason : ""),
      noticeDaysBefore: clampInt(o.noticeDaysBefore, 0, MAX_NOTICE_DAYS),
      resumeDays: normalizeResumeOverrides(o.resumeDays),
    });
  }
  out.sort((a, b) => (a.start === b.start ? a.id.localeCompare(b.id) : a.start.localeCompare(b.start)));
  return out;
}

/**
 * Migrate the legacy `holidays: {date, reason, blockOrders}[]` shape into
 * whole-day closures.
 *
 * `blockOrders` is **ignored**: every legacy entry, blocking or informational,
 * becomes a warn-and-shift closure. There is no hard block any more, so the flag
 * has nothing left to select. Read-only — the next admin save writes the new
 * shape and drops `holidays`.
 */
export function closuresFromLegacyHolidays(input: unknown): ClosurePeriod[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const raw: unknown[] = [];
  for (const item of input) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const stamp = parseLocalStamp(typeof o.date === "string" ? o.date.trim() : undefined);
    if (!stamp || seen.has(stamp.dateStr)) continue;
    seen.add(stamp.dateStr);
    raw.push({
      id: `holiday-${stamp.dateStr}`,
      start: `${stamp.dateStr}T00:00`,
      end: `${stamp.dateStr}T23:59`,
      reason: typeof o.reason === "string" ? o.reason : "",
    });
  }
  return normalizeClosures(raw);
}

/* ------------------------------------------------------------------ */
/*  Cart classification                                                */
/* ------------------------------------------------------------------ */

/**
 * Which product lines a cart spans, from its item categories.
 *
 * Unrecognised / absent categories are ignored rather than guessed at. An empty
 * result means "unknown cart", which every lead-time helper treats
 * conservatively (see `resolveResumeDays`).
 */
export function productLinesFromCategories(
  categories: readonly (string | null | undefined)[]
): ProductLine[] {
  const found = new Set<ProductLine>();
  for (const c of categories) {
    if (typeof c !== "string" || !c) continue;
    if (LEEDS_CATEGORIES.includes(c)) found.add("fresh");
    else if (PRESERVED_CATEGORIES.includes(c)) found.add("preserved");
    else if (CROCHET_CATEGORIES.includes(c)) found.add("crochet");
    else if (ACCESSORIES_CATEGORIES.includes(c)) found.add("accessories");
  }
  return PRODUCT_LINES.filter((l) => found.has(l));
}

/* ------------------------------------------------------------------ */
/*  Lead-time resolution                                               */
/* ------------------------------------------------------------------ */

/** Per-closure notice override, else the policy default. */
export function resolveNoticeDays(c: ClosurePeriod, policy: ClosurePolicy): number {
  return clampInt(c.noticeDaysBefore, 0, MAX_NOTICE_DAYS) ?? policy.noticeDaysBefore;
}

/**
 * Resume lead in days for the given cart lines — the LONGEST wins, because the
 * order ships as one job.
 *
 * An empty `lines` (unknown cart, or a context with no cart at all such as the
 * storefront banner) falls back to the longest lead across ALL lines. Being
 * conservative here can only over-state the wait; the opposite would offer a
 * date we can't fulfil.
 */
export function resolveResumeDays(
  c: ClosurePeriod,
  policy: ClosurePolicy,
  lines: readonly ProductLine[]
): number {
  const effective = lines.length > 0 ? lines : PRODUCT_LINES;
  let max = 0;
  for (const line of effective) {
    const override = clampInt(c.resumeDays?.[line], 0, MAX_RESUME_DAYS);
    const days = override ?? policy.resumeDays[line] ?? 0;
    if (days > max) max = days;
  }
  return max;
}

/* ------------------------------------------------------------------ */
/*  Dates                                                              */
/* ------------------------------------------------------------------ */

/** `YYYY-MM-DD` of the closure's first day, or `""` if malformed. */
export function closureStartDate(c: ClosurePeriod): string {
  return parseLocalStamp(c?.start)?.dateStr ?? "";
}

/** First date we can fulfil again = end calendar date + the resume lead. */
export function resumeDate(
  c: ClosurePeriod,
  policy: ClosurePolicy,
  lines: readonly ProductLine[]
): string {
  const end = parseLocalStamp(c?.end);
  if (!end) return "";
  return formatDateKey(addDays(end.day, resolveResumeDays(c, policy, lines)));
}

/**
 * The inclusive `[from … to]` block of unavailable calendar dates: the closure
 * itself plus its resume ramp.
 *
 * `to` is floored at the closure's own end date so a `resumeDays: 0` config
 * can't reopen the end date — rule 1 (any overlap closes the whole day) outranks
 * the ramp arithmetic. Returns empty strings for a malformed closure, which
 * `isDateUnavailable` treats as matching nothing.
 */
export function unavailableRange(
  c: ClosurePeriod,
  policy: ClosurePolicy,
  lines: readonly ProductLine[]
): { from: string; to: string } {
  const start = parseLocalStamp(c?.start);
  const end = parseLocalStamp(c?.end);
  if (!start || !end) return { from: "", to: "" };
  const resume = addDays(end.day, resolveResumeDays(c, policy, lines));
  const lastClosed = formatDateKey(addDays(resume, -1));
  return { from: start.dateStr, to: lastClosed < end.dateStr ? end.dateStr : lastClosed };
}

/** Is this calendar date inside ANY closure's unavailable range? */
export function isDateUnavailable(
  dateStr: string | null | undefined,
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  lines: readonly ProductLine[]
): boolean {
  const date = typeof dateStr === "string" ? dateStr.trim() : "";
  if (!date || !closures || closures.length === 0) return false;
  for (const c of closures) {
    const { from, to } = unavailableRange(c, policy, lines);
    if (!from || !to) continue;
    // ISO `YYYY-MM-DD` compares correctly as a string.
    if (date >= from && date <= to) return true;
  }
  return false;
}

/**
 * Walk forward from `base` to the first date in no unavailable range.
 *
 * When today sits inside a closure this lands exactly on the resume date.
 * Bounded by `horizonDays` so a pathological config (a closure with an absurd
 * resume lead, or a wall of overlapping closures) can't spin the picker; at the
 * bound it returns the horizon date rather than `base`, because handing back a
 * date we already know is closed is the worse of the two failures.
 */
export function earliestAvailableDate(
  base: Date,
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  lines: readonly ProductLine[],
  horizonDays: number = DEFAULT_HORIZON_DAYS
): Date {
  const start = startOfDay(base);
  if (Number.isNaN(start.getTime())) return start;
  if (!closures || closures.length === 0) return start;
  const limit = Math.max(0, Math.min(horizonDays, DEFAULT_HORIZON_DAYS));
  for (let i = 0; i <= limit; i++) {
    const candidate = addDays(start, i);
    if (!isDateUnavailable(formatDateKey(candidate), closures, policy, lines)) return candidate;
  }
  return addDays(start, limit);
}

/**
 * Every unavailable date in `[from … to]`, as `Date`s for react-day-picker's
 * `disabled` matcher (a `Date[]` is itself a valid Matcher, matched same-day).
 *
 * Hard-bounded to 365 days from `from`: the fresh picker has no upper bound
 * (`maxAdvanceDaysFresh` is unset by default), so without this a closure five
 * years out would ride along in the DOM.
 */
export function unavailableDatesInRange(
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  lines: readonly ProductLine[],
  from: Date,
  to: Date
): Date[] {
  if (!closures || closures.length === 0) return [];
  const start = startOfDay(from);
  const requestedEnd = startOfDay(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(requestedEnd.getTime())) return [];
  const horizonEnd = addDays(start, DEFAULT_HORIZON_DAYS);
  const end = requestedEnd.getTime() < horizonEnd.getTime() ? requestedEnd : horizonEnd;
  const out: Date[] = [];
  let cursor = start;
  while (cursor.getTime() <= end.getTime()) {
    if (isDateUnavailable(formatDateKey(cursor), closures, policy, lines)) out.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Announcement + liveness                                            */
/* ------------------------------------------------------------------ */

/**
 * Is the closure inside its announced window — `start − noticeDaysBefore` up to
 * and including its resume date?
 *
 * The resume date here is the CONSERVATIVE one (longest lead across all lines):
 * this decides visibility, not the customer's own date, so erring long keeps a
 * notice on screen for the line that is still waiting.
 */
export function isClosureAnnounced(
  c: ClosurePeriod,
  policy: ClosurePolicy,
  now: Date
): boolean {
  const start = parseLocalStamp(c?.start);
  const end = parseLocalStamp(c?.end);
  if (!start || !end) return false;
  const today = ukNow(now).dateStr;
  const announceFrom = formatDateKey(addDays(start.day, -resolveNoticeDays(c, policy)));
  const until = resumeDate(c, policy, []);
  if (!until) return false;
  return today >= announceFrom && today <= until;
}

/** Announced closures only, in start order. */
export function announcedClosures(
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  now: Date
): ClosurePeriod[] {
  if (!closures || closures.length === 0) return [];
  return closures
    .filter((c) => isClosureAnnounced(c, policy, now))
    .slice()
    .sort((a, b) => a.start.localeCompare(b.start));
}

/* ------------------------------------------------------------------ */
/*  Customer-facing copy                                               */
/* ------------------------------------------------------------------ */

/** "5pm" / "5:30pm" / "12am". */
function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const mi = minutes % 60;
  const period = h < 12 ? "am" : "pm";
  const display = h % 12 === 0 ? 12 : h % 12;
  return mi === 0 ? `${display}${period}` : `${display}:${String(mi).padStart(2, "0")}${period}`;
}

/** "24 Dec" — deliberately locale-independent, so the copy can't drift by host. */
function formatDayMonth(d: Date): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** Human-readable date for a `YYYY-MM-DD` key, e.g. "4 Jan". */
function formatDateKeyShort(key: string): string {
  const s = parseLocalStamp(key);
  return s ? formatDayMonth(s.day) : key;
}

/**
 * "24 Dec 5pm – 2 Jan 9am".
 *
 * A whole-day closure (00:00 → 23:59, which is what every migrated legacy
 * holiday is) drops the times entirely — "25 Dec", or "24 Dec – 2 Jan" — because
 * "25 Dec 12am – 25 Dec 11:59pm" is noise, not precision.
 */
export function formatClosureWindow(c: ClosurePeriod): string {
  const start = parseLocalStamp(c?.start);
  const end = parseLocalStamp(c?.end);
  if (!start || !end) return "";
  const sameDay = start.dateStr === end.dateStr;
  const wholeDay = start.minutes === 0 && end.minutes >= 23 * 60 + 59;
  if (wholeDay) {
    return sameDay
      ? formatDayMonth(start.day)
      : `${formatDayMonth(start.day)} – ${formatDayMonth(end.day)}`;
  }
  const left = `${formatDayMonth(start.day)} ${formatTime(start.minutes)}`;
  const right = sameDay
    ? formatTime(end.minutes)
    : `${formatDayMonth(end.day)} ${formatTime(end.minutes)}`;
  return `${left} – ${right}`;
}

const LINE_LABELS: Record<ProductLine, string> = {
  fresh: "Fresh",
  preserved: "Preserved",
  crochet: "Crochet",
  accessories: "Accessories",
};

/**
 * The warning shown above the checkout date field whenever an announced closure
 * is in play, e.g.
 *
 *   "We're closed 24 Dec 5pm – 2 Jan 9am (Christmas). Fresh orders resume 4 Jan
 *    — your order will be prepared on the date you choose."
 *
 * The resume date is for the lines ACTUALLY IN THE CART, so a crochet-only cart
 * is never told to wait for fresh's longer lead. The line is named only when the
 * cart is single-line; naming one of several would be a half-truth.
 *
 * Returns null when nothing is announced.
 */
export function closureWarning(
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  lines: readonly ProductLine[],
  now: Date
): string | null {
  const announced = announcedClosures(closures, policy, now);
  if (announced.length === 0) return null;
  const c = announced[0];
  const window = formatClosureWindow(c);
  if (!window) return null;
  const reason = sanitiseClosureReason(c.reason);
  const resume = resumeDate(c, policy, lines);
  const subject = lines.length === 1 ? `${LINE_LABELS[lines[0]]} orders` : "Orders";
  const first = reason ? `We're closed ${window} (${reason}).` : `We're closed ${window}.`;
  if (!resume) return first;
  return `${first} ${subject} resume ${formatDateKeyShort(resume)} — your order will be prepared on the date you choose.`;
}

/**
 * The subset of closures safe to serve on the PUBLIC settings endpoint:
 * announced ones only, reasons re-sanitised to 80 characters.
 *
 * Un-announced future closures are withheld (the admin has not chosen to tell
 * anyone yet) and past ones fall out of the announced window on their own.
 */
export function publicClosures(
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  now: Date
): ClosurePeriod[] {
  return announcedClosures(closures, policy, now).map((c) => ({
    ...c,
    reason: sanitiseClosureReason(c.reason),
  }));
}

/**
 * The closures the checkout PICKER must know about: **every** closure whose
 * unavailable block has not yet finished — announced or not — with the
 * free-text `reason` (and the admin-authored `id`) withheld until it is.
 *
 * ⚠ Deliberately WIDER than `publicClosures`, and the two are not
 * interchangeable. **Announcement gates the PROSE, never the disabled dates.**
 * Serving the announced subset to the picker was CLOSURE-A (fixed 2026-08-14):
 * with the default `noticeDaysBefore: 14` a customer shopping 30 days ahead met
 * no bar, no alert and an entirely un-greyed calendar, and could book straight
 * into a day the florist is shut — the florist then receiving an order for a
 * closed day with the customer having had zero signal. A day we cannot fulfil
 * has to be greyed out from the moment it is configured; only the SENTENCE
 * waits for the notice window.
 *
 * What is withheld while un-announced is the free text, not the dates. The
 * reason is the admin's private business until they choose to publish it
 * ("Surgery", "Family funeral", "Summer break — do not announce yet") and this
 * lands in a PUBLIC JSON response. The `id` is stripped with it, because it is
 * admin-authored on the same document and leaks exactly the same thing; a
 * deterministic date-derived id replaces it so the shape stays usable as a
 * React key and a lookup handle.
 *
 * Nothing downstream has to change to keep the prose correct: `closureWarning`
 * and `ClosureNoticeClient` both re-run `announcedClosures` over whatever they
 * are handed, so an un-announced row served here renders no sentence anywhere.
 *
 * There is deliberately **no forward horizon**. `unavailableDatesInRange`
 * already bounds the `Date[]` handed to the picker at `DEFAULT_HORIZON_DAYS`,
 * so a distant closure costs ~150 bytes of JSON and nothing else — whereas a
 * cut-off here would re-open the very hole this function exists to close, just
 * further out.
 */
export function pickerClosures(
  closures: readonly ClosurePeriod[] | null | undefined,
  policy: ClosurePolicy,
  now: Date
): ClosurePeriod[] {
  if (!closures || closures.length === 0) return [];
  const today = ukNow(now).dateStr;
  const out: ClosurePeriod[] = [];
  for (const c of closures) {
    const start = parseLocalStamp(c?.start);
    const end = parseLocalStamp(c?.end);
    // Malformed rows are DROPPED, exactly as `normalizeClosures` drops them: an
    // Invalid Date reaching the picker's disabled matcher disables nothing and
    // reports nothing, which is worse than an absent closure.
    if (!start || !end || end.ordinal < start.ordinal) continue;
    // Still relevant until every product line has reopened (the conservative
    // longest lead) — the same upper bound `isClosureAnnounced` uses, so the
    // two functions can never disagree about when a closure is finished.
    const until = resumeDate(c, policy, []);
    if (!until || until < today) continue;
    const announced = isClosureAnnounced(c, policy, now);
    const syntheticId = `closure-${formatStamp(start).replace(/\D/g, "")}-${formatStamp(end).replace(/\D/g, "")}`;
    out.push({
      ...c,
      id:
        announced && typeof c.id === "string" && c.id.trim()
          ? c.id.trim()
          : syntheticId,
      start: formatStamp(start),
      end: formatStamp(end),
      reason: announced ? sanitiseClosureReason(c.reason) : "",
    });
  }
  return out.sort((a, b) =>
    a.start === b.start ? a.id.localeCompare(b.id) : a.start.localeCompare(b.start)
  );
}
